// Politique de confidentialité, version FR

const privacyPolicyFr = `# Politique de confidentialité

*Dernière mise à jour : 13 mai 2026*

---

## Préambule

La présente Politique de confidentialité décrit la manière dont **BPM**, Société par Actions Simplifiée (SAS) au capital social de 1 000 €, immatriculée au RCS de NANTERRE sous le numéro 987 605 567, dont le siège social est situé au 73, Rue du Château, 92100 BOULOGNE-BILLANCOURT (ci-après « BPM », « nous » ou « notre »), collecte, utilise, partage et protège les données personnelles des utilisateurs (ci-après « vous » ou « l'utilisateur ») du site **beyondpersonalmatrix.com** et du service **Beyond Personal Matrix** (ci-après « le Service »).

BPM s'engage à respecter le Règlement Général sur la Protection des Données (UE) 2016/679 (« RGPD ») et la loi française n° 78-17 du 6 janvier 1978 modifiée, dite « Informatique et Libertés ».

---

## 1. Responsable du traitement

Le responsable du traitement des données personnelles est :

**BPM**
73, Rue du Château, 92100 BOULOGNE-BILLANCOURT (France)
SIREN : 987 605 567
Courriel : privacy@beyondpersonalmatrix.com

BPM n'a pas désigné de Délégué à la Protection des Données (DPO) à ce jour, sa structure et ses activités ne rendant pas cette désignation obligatoire au sens de l'article 37 du RGPD.

---

## 2. Données personnelles collectées

Dans le cadre de l'utilisation du Service, nous collectons les catégories de données suivantes :

### 2.1 Données fournies directement par l'utilisateur

- **Identité** : prénom, nom (si renseigné)
- **Coordonnées** : adresse électronique
- **Données de paiement** : traitées exclusivement par notre prestataire Stripe (BPM ne stocke ni le numéro de carte bancaire, ni le cryptogramme)
- **Réponses aux tests psychométriques** : réponses fournies aux questionnaires DISC, Big Five, MBTI, Ennéagramme, VIA Strengths, Valeurs Schwartz
- **Données du partenaire (pack Duo uniquement)** : adresse électronique du partenaire invité

### 2.2 Données collectées automatiquement

- **Données techniques** : adresse IP, type de navigateur, système d'exploitation, langue de navigation, fuseau horaire
- **Données de connexion** : dates et heures de connexion, durée de session
- **Données d'utilisation** : pages consultées, fonctionnalités utilisées, interactions avec le Service
- **Cookies** : voir notre Politique relative aux cookies présentée lors de votre première visite

### 2.3 Données générées par le Service

- **Résultats psychométriques** : scores, profils, recommandations générés à partir de vos réponses
- **Rapports d'analyse IA** : interprétations produites par nos modèles d'intelligence artificielle à partir de vos résultats

---

## 3. Finalités et bases légales du traitement

| Finalité | Base légale (RGPD) |
|---|---|
| Création et gestion de votre compte utilisateur | Exécution du contrat (art. 6.1.b) |
| Fourniture du Service (tests, résultats, rapports) | Exécution du contrat (art. 6.1.b) |
| Traitement des paiements | Exécution du contrat (art. 6.1.b) |
| Envoi d'emails transactionnels (activation, confirmation) | Exécution du contrat (art. 6.1.b) |
| Facturation et obligations comptables | Obligation légale (art. 6.1.c) |
| Support client | Intérêt légitime (art. 6.1.f) |
| Mesure d'audience et amélioration du Service | Intérêt légitime (art. 6.1.f) ou consentement (art. 6.1.a) selon les cookies |
| Lutte contre la fraude | Intérêt légitime (art. 6.1.f) |
| Envoi de communications marketing | Consentement (art. 6.1.a), révocable à tout moment |

---

## 4. Destinataires et sous-traitants

Vos données personnelles sont accessibles, dans la limite de leurs attributions respectives, à :

- **Personnel autorisé de BPM** : pour l'exécution de leurs missions
- **Sous-traitants techniques** listés ci-dessous, dans la stricte mesure nécessaire à l'exécution du Service

### Liste des sous-traitants

| Sous-traitant | Finalité | Localisation | Garanties |
|---|---|---|---|
| **Emergent** *(Emergent Methods, Inc.)* | Hébergement du Site et du backend | États-Unis | Clauses contractuelles types de la Commission européenne |
| **Stripe Payments Europe Ltd.** | Traitement des paiements | Irlande (UE) + États-Unis (transferts) | Clauses contractuelles types + Data Processing Agreement |
| **Brevo** *(Sendinblue SAS)* | Envoi d'emails transactionnels | France (UE) | Sous-traitant UE, conforme RGPD nativement |
| **MongoDB Atlas** | Stockage de la base de données | UE *(à confirmer selon la région choisie)* | Clauses contractuelles types + Data Processing Agreement |
| **PostHog** | Mesure d'audience anonymisée | UE (eu.i.posthog.com) | Sous-traitant UE, conforme RGPD nativement |
| **Google Analytics 4** *(Google Ireland Ltd.)* | Mesure d'audience | Irlande (UE) + États-Unis (transferts) | Clauses contractuelles types + anonymisation IP |
| **Google Workspace** *(Google Ireland Ltd.)* | Messagerie professionnelle support@ et noreply@ | Irlande (UE) + États-Unis | Clauses contractuelles types + Data Processing Agreement |
| **Namecheap, Inc.** | Enregistrement du nom de domaine | États-Unis | Pas d'accès aux données personnelles des utilisateurs du Service |

---

## 5. Transferts hors Union européenne

Certains de nos sous-traitants sont susceptibles de transférer ou de traiter vos données aux États-Unis (Emergent, Stripe pour certaines fonctions, Google Workspace pour certaines fonctions, Namecheap).

Ces transferts sont encadrés par les **clauses contractuelles types** adoptées par la Commission européenne (décision 2021/914), garantissant un niveau de protection des données équivalent à celui de l'Union européenne.

---

## 6. Durées de conservation

| Catégorie de données | Durée de conservation |
|---|---|
| Données de compte (email, prénom) | Pendant la durée de l'abonnement, puis 3 ans après la dernière activité (prospection) |
| Réponses aux tests | Pendant la durée de l'abonnement, puis 3 ans pour analyse statistique anonymisée |
| Résultats et rapports IA | Pendant la durée de l'abonnement |
| Données de paiement | Non stockées par BPM (Stripe les conserve conformément à ses obligations PCI-DSS) |
| Factures et données comptables | 10 ans à compter de la clôture de l'exercice (article L.123-22 du Code de commerce) |
| Logs techniques | 12 mois maximum |
| Cookies | Selon préférences utilisateur, max. 13 mois |

À l'issue de ces durées, vos données sont supprimées ou anonymisées.

---

## 7. Vos droits

Conformément au RGPD, vous disposez des droits suivants :

- **Droit d'accès** (art. 15) : obtenir confirmation que des données vous concernant sont traitées et en obtenir une copie
- **Droit de rectification** (art. 16) : faire corriger des données inexactes ou incomplètes
- **Droit à l'effacement** (« droit à l'oubli », art. 17) : faire supprimer vos données dans les cas prévus par le RGPD
- **Droit à la limitation du traitement** (art. 18)
- **Droit à la portabilité** (art. 20) : recevoir vos données dans un format structuré et lisible par machine
- **Droit d'opposition** (art. 21) : vous opposer au traitement pour des raisons tenant à votre situation particulière, ou à tout moment pour la prospection commerciale
- **Droit de retirer votre consentement** à tout moment lorsque le traitement est fondé sur celui-ci
- **Droit de définir des directives** relatives au sort de vos données après votre décès (article 85 de la loi Informatique et Libertés)

### Comment exercer vos droits

Pour exercer ces droits, vous pouvez nous contacter à : **privacy@beyondpersonalmatrix.com**.

Nous répondrons dans un délai d'**un mois** à compter de la réception de votre demande (délai pouvant être étendu de deux mois supplémentaires en cas de demande complexe, conformément à l'article 12 du RGPD).

Une preuve d'identité pourra vous être demandée en cas de doute raisonnable sur votre identité.

### Réclamation auprès d'une autorité de contrôle

Si vous estimez, après nous avoir contactés, que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de la **Commission Nationale de l'Informatique et des Libertés (CNIL)** :
3 Place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07
Site internet : [www.cnil.fr](https://www.cnil.fr)

---

## 8. Sécurité

BPM met en œuvre des mesures techniques et organisationnelles appropriées pour protéger vos données personnelles contre la perte, l'altération, la divulgation ou l'accès non autorisé. Ces mesures incluent notamment :

- Chiffrement des communications via TLS (HTTPS)
- Chiffrement des mots de passe (hashage bcrypt)
- Accès aux données limité aux personnes habilitées
- Surveillance des accès et journalisation
- Sauvegardes régulières de la base de données
- Mises à jour de sécurité régulières

Aucun système n'étant totalement infaillible, BPM ne peut garantir une sécurité absolue. En cas de violation de données susceptible d'engendrer un risque pour vos droits et libertés, BPM s'engage à notifier la CNIL dans un délai de 72 heures conformément à l'article 33 du RGPD, et à vous informer dans les meilleurs délais conformément à l'article 34.

---

## 9. Cookies

Le Site utilise des cookies et technologies similaires. La gestion de ces cookies fait l'objet d'une politique distincte, accessible via le bandeau de gestion des cookies présenté lors de votre première visite. Vous pouvez à tout moment modifier vos préférences.

Sont utilisés notamment :
- **Cookies strictement nécessaires** (authentification, panier, langue) : aucun consentement requis
- **Cookies de mesure d'audience** (PostHog en mode RGPD eu.i.posthog.com, Google Analytics 4 avec anonymisation IP) : soumis à votre consentement
- **Cookies tiers de paiement** (Stripe) : strictement nécessaires à la finalisation du paiement

---

## 10. Mineurs

Le Service n'est pas destiné aux mineurs de moins de 15 ans (seuil de majorité numérique en France au sens de l'article 8 du RGPD). Si vous avez moins de 15 ans, vous devez obtenir le consentement de vos parents ou tuteurs légaux avant d'utiliser le Service.

BPM se réserve le droit de demander une preuve d'âge en cas de doute raisonnable, et de supprimer le compte d'un utilisateur dont la minorité serait avérée sans consentement parental.

---

## 11. Modifications

BPM se réserve le droit de modifier la présente Politique de confidentialité à tout moment. Toute modification substantielle vous sera notifiée par email et/ou par bandeau d'information sur le Site, avec un préavis raisonnable avant entrée en vigueur. La poursuite de l'utilisation du Service après notification vaut acceptation de la version modifiée.

La date de dernière mise à jour figure en haut du présent document.

---

## 12. Contact

Pour toute question relative à la présente Politique de confidentialité ou au traitement de vos données personnelles :

**BPM**
73, Rue du Château, 92100 BOULOGNE-BILLANCOURT
Courriel : privacy@beyondpersonalmatrix.com
`;

export default privacyPolicyFr;
